/**
 * Auth Callback Page
 *
 * Handles the OAuth callback from WorkOS AuthKit.
 * After WorkOS authentication, users are redirected here.
 * The AuthKit SDK automatically handles the token exchange.
 *
 * This component:
 * 1. Shows a loading state while AuthKit processes the callback
 * 2. Redirects to the appropriate dashboard based on user role
 */
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@shared/context';

/**
 * Get redirect path based on user roles
 */
function getDefaultRouteForRoles(roles: string[] = []): string {
  // Priority order: ADMIN roles first, then staff, then customer
  if (roles.includes('SUPER_ADMIN') || roles.includes('ADMIN')) {
    return '/admin/transactions';
  }
  if (roles.includes('CASE_MANAGER') || roles.includes('COMPLIANCE')) {
    return '/case-manager/dashboard';
  }
  return '/accounts/me';
}

// How long to wait for auth to complete before showing error (ms)
const AUTH_TIMEOUT_MS = 10000;

export default function AuthCallback() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [hasError, setHasError] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasRedirectedRef = useRef(false);

  // Set up a timeout - if auth doesn't complete in time, show error
  useEffect(() => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');

    // If there's an explicit error param from WorkOS, show error immediately
    if (error) {
      setHasError(true);
      return;
    }

    // Only set timeout if we have a code (i.e., we're in the middle of auth flow)
    if (code && !hasRedirectedRef.current) {
      timeoutRef.current = setTimeout(() => {
        // Only show error if we still haven't authenticated
        if (!hasRedirectedRef.current) {
          console.warn('[AuthCallback] Auth timeout - showing error');
          setHasError(true);
        }
      }, AUTH_TIMEOUT_MS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchParams]);

  // Handle auth state changes - redirect when ready
  useEffect(() => {
    const code = searchParams.get('code');

    // Wait for auth to complete loading
    if (isLoading) {
      return;
    }

    if (isAuthenticated && user) {
      // Success! Clear timeout and redirect
      hasRedirectedRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      const redirectPath = getDefaultRouteForRoles(user.roles);
      navigate(redirectPath, { replace: true });
    } else if (!code && !isAuthenticated) {
      // No code and no auth - this isn't a callback, redirect to login
      navigate('/login', { replace: true });
    }
    // If we have a code but aren't authenticated yet, just wait - 
    // the timeout will handle the error case
  }, [isAuthenticated, isLoading, user, navigate, searchParams]);

  if (hasError) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          fontFamily: 'system-ui, sans-serif',
        }}
      >
        <h2 style={{ color: '#d32f2f', marginBottom: 16 }}>Authentication Failed</h2>
        <p style={{ color: '#666', marginBottom: 24, textAlign: 'center', maxWidth: 400 }}>
          There was a problem signing you in. This can happen if the sign-in session expired.
        </p>
        <button
          onClick={() => {
            // Clear any stale state and go to login
            localStorage.removeItem('token');
            window.location.href = '/login';
          }}
          style={{
            padding: '12px 24px',
            backgroundColor: '#28dab6',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            fontSize: 16,
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div className="spinner" aria-label="Loading..." />
      <p style={{ marginTop: 16, color: '#666' }}>Completing sign in...</p>
    </div>
  );
}
